import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import Drawer, { Field } from "@/components/Drawer";
import { Plus, Trash2, Pencil, CheckCircle2, Circle, Bell } from "lucide-react";
import { fmtDateTime } from "@/lib/format";
import { toast } from "sonner";

const empty = {
  title: "",
  type: "call",
  due_date: "",
  notes: "",
  lead_id: "",
  deal_id: "",
  customer_id: "",
  assigned_to: "",
  completed: false,
};

export default function FollowUps() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [deals, setDeals] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [drawer, setDrawer] = useState(null);
  const [tab, setTab] = useState("upcoming");

  const load = async () => {
    const [r, u, l, d, c] = await Promise.all([
      api.get("/follow-ups"),
      api.get("/users"),
      api.get("/leads"),
      api.get("/deals"),
      api.get("/customers"),
    ]);
    setRows(r.data);
    setUsers(u.data);
    setLeads(l.data);
    setDeals(d.data);
    setCustomers(c.data);
  };
  useEffect(() => { load(); }, []);

  const now = new Date();
  const isOverdue = (r) => !r.completed && r.due_date && new Date(r.due_date) < now;
  const filtered = rows
    .filter((r) => {
      if (tab === "upcoming") return !r.completed && !isOverdue(r);
      if (tab === "overdue") return isOverdue(r);
      if (tab === "completed") return r.completed;
      return true;
    })
    .sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""));

  const toggle = async (r) => {
    await api.patch(`/follow-ups/${r.id}`, { ...r, completed: !r.completed });
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete follow-up?")) return;
    await api.delete(`/follow-ups/${id}`);
    load();
  };
  const save = async (data) => {
    const payload = {
      ...data,
      lead_id: data.lead_id || null,
      deal_id: data.deal_id || null,
      customer_id: data.customer_id || null,
      assigned_to: data.assigned_to || null,
    };
    try {
      if (drawer.mode === "edit") {
        await api.patch(`/follow-ups/${drawer.data.id}`, payload);
      } else {
        await api.post("/follow-ups", payload);
      }
      toast.success("Saved");
      setDrawer(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div data-testid="followups-page">
      <PageHeader
        title="Follow-ups"
        subtitle="Never miss a call, meeting or site visit."
        actions={
          <button className="btn-primary flex items-center gap-2" data-testid="add-followup-btn"
            onClick={() => setDrawer({ mode: "create", data: { ...empty, due_date: new Date().toISOString().slice(0, 16) } })}>
            <Plus className="w-4 h-4" /> New Follow-up
          </button>
        }
      />

      <div className="flex gap-1 mb-4 bg-white rounded-lg p-1 border border-slate-200 w-fit">
        {["upcoming", "overdue", "completed", "all"].map((t) => (
          <button
            key={t}
            data-testid={`tab-${t}`}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm rounded-md capitalize transition-colors ${
              tab === t ? "bg-[#0F3A66] text-white" : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            {t} ({t === "upcoming" ? rows.filter((r) => !r.completed && !isOverdue(r)).length
              : t === "overdue" ? rows.filter(isOverdue).length
              : t === "completed" ? rows.filter((r) => r.completed).length
              : rows.length})
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((r) => {
          const overdue = isOverdue(r);
          return (
            <div key={r.id} className="crm-card p-4" data-testid={`followup-card-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <button
                  onClick={() => toggle(r)}
                  className="mt-0.5 shrink-0"
                  data-testid={`toggle-followup-${r.id}`}
                >
                  {r.completed
                    ? <CheckCircle2 className="w-5 h-5 text-[#10B981]" />
                    : <Circle className={`w-5 h-5 ${overdue ? "text-red-500" : "text-slate-400"}`} />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className={`font-medium ${r.completed ? "line-through text-slate-400" : "text-slate-900"}`}>
                    {r.title}
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-xs">
                    <span className="badge bg-slate-100 text-slate-600 capitalize">{r.type.replace("_", " ")}</span>
                    <span className={`inline-flex items-center gap-1 ${overdue ? "text-red-600 font-medium" : "text-slate-500"}`}>
                      <Bell className="w-3 h-3" /> {fmtDateTime(r.due_date)}
                    </span>
                  </div>
                  {r.notes && <p className="text-xs text-slate-500 mt-2 line-clamp-2">{r.notes}</p>}
                </div>
                <div className="flex gap-1 shrink-0">
                  <button className="btn-ghost !p-1.5" data-testid={`edit-followup-${r.id}`}
                    onClick={() => setDrawer({ mode: "edit", data: { ...empty, ...r, due_date: r.due_date?.slice(0, 16) || "" } })}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button className="btn-ghost !p-1.5 text-red-600" data-testid={`delete-followup-${r.id}`}
                    onClick={() => remove(r.id)}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="col-span-full text-center text-slate-500 py-12 border border-dashed rounded-lg">
            Nothing here. Create a new follow-up to get started.
          </div>
        )}
      </div>

      {drawer && (
        <FollowUpDrawer
          drawer={drawer}
          onClose={() => setDrawer(null)}
          onSave={save}
          users={users}
          leads={leads}
          deals={deals}
          customers={customers}
        />
      )}
    </div>
  );
}

function FollowUpDrawer({ drawer, onClose, onSave, users, leads, deals, customers }) {
  const [data, setData] = useState(drawer.data);
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  return (
    <Drawer open onClose={onClose} title={drawer.mode === "edit" ? "Edit Follow-up" : "New Follow-up"}
      testid="followup-drawer"
      footer={
        <>
          <button className="btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn-primary" data-testid="followup-save-btn" onClick={() => onSave(data)}>Save</button>
        </>
      }
    >
      <Field label="Title *"><input className="input-base" value={data.title} data-testid="followup-title" onChange={(e) => set("title", e.target.value)} /></Field>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
        <Field label="Type">
          <select className="input-base" value={data.type} data-testid="followup-type" onChange={(e) => set("type", e.target.value)}>
            {["call", "meeting", "email", "site_visit", "other"].map((k) => <option key={k} value={k}>{k.replace("_", " ")}</option>)}
          </select>
        </Field>
        <Field label="Due Date *">
          <input type="datetime-local" className="input-base" value={data.due_date || ""} data-testid="followup-due" onChange={(e) => set("due_date", e.target.value)} />
        </Field>
        <Field label="Assign To">
          <select className="input-base" value={data.assigned_to || ""} onChange={(e) => set("assigned_to", e.target.value)}>
            <option value="">— Unassigned —</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </Field>
        <Field label="Linked Lead">
          <select className="input-base" value={data.lead_id || ""} onChange={(e) => set("lead_id", e.target.value)}>
            <option value="">—</option>
            {leads.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </Field>
        <Field label="Linked Deal">
          <select className="input-base" value={data.deal_id || ""} onChange={(e) => set("deal_id", e.target.value)}>
            <option value="">—</option>
            {deals.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
          </select>
        </Field>
        <Field label="Linked Customer">
          <select className="input-base" value={data.customer_id || ""} onChange={(e) => set("customer_id", e.target.value)}>
            <option value="">—</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Notes">
        <textarea className="input-base min-h-[100px]" value={data.notes || ""} onChange={(e) => set("notes", e.target.value)} />
      </Field>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={data.completed} onChange={(e) => set("completed", e.target.checked)} />
        Mark as completed
      </label>
    </Drawer>
  );
}
